import { fullFormHexToVecRGBA, type VecRGBA } from "utils/color";

/**
 * The fixed set of environment choices exposed in the Settings dialog.
 * "Concrete" and "Ocean" are IBL skyboxes hosted alongside the other Novorender
 * environment assets (no blur applied). "Dark", "Grey" and "Light (Legacy)" are
 * flat 2D background colors with no skybox texture (engine-default IBL lighting).
 */
export type EnvironmentPresetId = "concrete" | "ocean" | "dark" | "grey" | "light";

/** Flat 2D background color hex codes used by the non-skybox presets. */
export const ENVIRONMENT_PRESET_HEX = {
    dark: "#2F3437",
    grey: "#919CA1",
    light: "#EFF6FA",
} as const;

type SkyboxPreset = {
    kind: "skybox";
    id: EnvironmentPresetId;
    /** Asset folder name, e.g. `${assetsUrl}/env/<envName>/` */
    envName: string;
    labelKey: string;
};

type FlatPreset = {
    kind: "flat";
    id: EnvironmentPresetId;
    color: VecRGBA;
    labelKey: string;
};

type EnvironmentPreset = SkyboxPreset | FlatPreset;

export const ENVIRONMENT_PRESETS: EnvironmentPreset[] = [
    { kind: "skybox", id: "concrete", envName: "concrete", labelKey: "env.concrete" },
    { kind: "skybox", id: "ocean", envName: "ocean", labelKey: "env.ocean" },
    { kind: "flat", id: "dark", color: fullFormHexToVecRGBA(ENVIRONMENT_PRESET_HEX.dark, 1), labelKey: "env.dark" },
    { kind: "flat", id: "grey", color: fullFormHexToVecRGBA(ENVIRONMENT_PRESET_HEX.grey, 1), labelKey: "env.grey" },
    {
        kind: "flat",
        id: "light",
        color: fullFormHexToVecRGBA(ENVIRONMENT_PRESET_HEX.light, 1),
        labelKey: "env.light",
    },
];

/** Flat Light (Legacy) background color -- the default when no environment has been configured. */
export const LIGHT_LEGACY_COLOR: VecRGBA = fullFormHexToVecRGBA(ENVIRONMENT_PRESET_HEX.light, 1);

export const DEFAULT_ENVIRONMENT_PRESET_ID: EnvironmentPresetId = "light";

function getSkyboxUrl(preset: SkyboxPreset, assetsUrl: string): string {
    return `${assetsUrl}/env/${preset.envName}/`;
}

/** Resolves a preset id into the `background` state patch that should be dispatched to select it. */
export function getEnvironmentPresetBackground(
    id: EnvironmentPresetId,
    assetsUrl: string,
): { url: string; color?: VecRGBA; blur?: number } {
    const preset = ENVIRONMENT_PRESETS.find((p) => p.id === id);
    if (!preset) {
        return { url: "" };
    }

    return preset.kind === "skybox"
        ? { url: getSkyboxUrl(preset, assetsUrl), blur: 0 }
        : { url: "", color: preset.color };
}

function colorsApproximatelyEqual(a: VecRGBA, b: VecRGBA, epsilon = 0.001): boolean {
    return a.every((v, i) => Math.abs(v - b[i]) < epsilon);
}

/**
 * Determines which (if any) of the fixed presets matches the current background state.
 * Returns `undefined` when the current background was configured outside of the standard
 * presets, e.g. a custom environment picked by an Admin via Advanced Settings.
 */
export function getEnvironmentPresetId(
    background: { url: string; color: VecRGBA },
    assetsUrl: string,
): EnvironmentPresetId | undefined {
    for (const preset of ENVIRONMENT_PRESETS) {
        if (preset.kind === "skybox") {
            if (background.url === getSkyboxUrl(preset, assetsUrl)) {
                return preset.id;
            }
        } else if (!background.url && colorsApproximatelyEqual(background.color, preset.color)) {
            return preset.id;
        }
    }

    return undefined;
}
